/**
 * HR/Admin KPI Linkage Manager: create parent-child KPI links, set weights,
 * and browse the hierarchy tree. Not shown to employees or managers.
 */
// React imports
import React, { useEffect, useMemo, useState } from "react";

// External library imports
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  deleteData,
  fetchData,
  postData,
  putData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import {
  kpiLinkageDetailUrl,
  kpiLinkageTreeUrl,
  kpiLinkagesUrl,
  kpiMasterUrl,
} from "../../../Service/Apis";
import KPITreeView, { KPITreeNode } from "./components/KPITreeView";
import { StandardTablePagination } from "../../../components/Pagination/StandardTablePagination";
import { PAGINATION_CONFIG } from "../../../utils/paginationConfig";

/** KPI option for the linkage parent/child dropdowns. */
interface KPIMasterOption {
  kpi_id: string;
  kpi_code: string | null;
  question: string;
  threshold: number | null;
  weightage: number;
  section_name: string;
  template_name: string;
  designation_name: string;
}

/** Persisted parent-child KPI linkage row from the API. */
interface KPILinkage {
  linkage_id: string;
  child_kpi: string;
  child_kpi_code: string | null;
  child_kpi_title: string;
  parent_kpi: string;
  parent_kpi_code: string | null;
  parent_kpi_title: string;
  weight: number;
  cycle_start_date: string | null;
  cycle_end_date: string | null;
  is_active: boolean;
  created_by_name: string | null;
  created_date: string;
  weight_warning: string | null;
}

/** Create/edit form state for a single KPI linkage. */
interface LinkageFormState {
  linkage_id: string | null;
  child_kpi: string;
  parent_kpi: string;
  weight: string;
  cycle_start_date: string;
  cycle_end_date: string;
}

const emptyForm: LinkageFormState = {
  linkage_id: null,
  child_kpi: "",
  parent_kpi: "",
  weight: "",
  cycle_start_date: "",
  cycle_end_date: "",
};

/** Short label for a KPI in selects: code plus truncated title. */
const kpiLabel = (kpi: KPIMasterOption): string =>
  `${kpi.kpi_code || "(no code)"} — ${kpi.question.slice(0, 60)}`;

/**
 * HR/Admin screen to manage KPI parent-child linkages and view the tree.
 */
const KPILinkageManager: React.FC = () => {
  const showToast = useCustomToast();
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const [kpis, setKpis] = useState<KPIMasterOption[]>([]);
  const [linkages, setLinkages] = useState<KPILinkage[]>([]);
  const [tree, setTree] = useState<KPITreeNode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedKpiId, setSelectedKpiId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [form, setForm] = useState<LinkageFormState>(emptyForm);
  const [saving, setSaving] = useState<boolean>(false);

  const [deleteTarget, setDeleteTarget] = useState<KPILinkage | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);

  const handleShowToast = (message: string, variant: ToastVariant): void => {
    showToast(message, variant, true);
  };

  const kpiById = useMemo(() => {
    const map: Record<string, KPIMasterOption> = {};
    kpis.forEach((k) => (map[k.kpi_id] = k));
    return map;
  }, [kpis]);

  const pagedLinkages = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return linkages.slice(start, start + pageSize);
  }, [linkages, currentPage, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(linkages.length / pageSize) || 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [linkages.length, pageSize, currentPage]);

  const fetchAll = async (): Promise<void> => {
    setLoading(true);
    try {
      const [kpiResponse, linkageResponse, treeResponse] = await Promise.all([
        dispatch(fetchData(kpiMasterUrl)),
        dispatch(fetchData(kpiLinkagesUrl)),
        dispatch(fetchData(kpiLinkageTreeUrl)),
      ]);

      setKpis(Array.isArray(kpiResponse) ? kpiResponse : kpiResponse?.results || []);
      setLinkages(Array.isArray(linkageResponse) ? linkageResponse : linkageResponse?.results || []);
      setTree(treeResponse?.tree || []);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      handleShowToast(
        axiosError.response?.data?.error || axiosError.message || "Failed to load KPI linkage data",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateForm = (): void => {
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEditForm = (linkage: KPILinkage): void => {
    setForm({
      linkage_id: linkage.linkage_id,
      child_kpi: linkage.child_kpi,
      parent_kpi: linkage.parent_kpi,
      weight: String(linkage.weight),
      cycle_start_date: linkage.cycle_start_date || "",
      cycle_end_date: linkage.cycle_end_date || "",
    });
    setFormOpen(true);
  };

  const handleSelectChange = (field: "child_kpi" | "parent_kpi") => (e: SelectChangeEvent<string>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async (): Promise<void> => {
    if (!form.child_kpi || !form.parent_kpi) {
      handleShowToast("Please select both a Child KPI and a Parent KPI.", "error");
      return;
    }
    if (form.child_kpi === form.parent_kpi) {
      handleShowToast("A KPI cannot link to itself.", "error");
      return;
    }
    const weightValue = parseFloat(form.weight);
    if (Number.isNaN(weightValue) || weightValue <= 0) {
      handleShowToast("Please enter a weight greater than 0 (e.g. 0.33 or 0.5).", "error");
      return;
    }

    const payload = {
      child_kpi: form.child_kpi,
      parent_kpi: form.parent_kpi,
      weight: weightValue,
      cycle_start_date: form.cycle_start_date || null,
      cycle_end_date: form.cycle_end_date || null,
    };

    setSaving(true);
    try {
      if (form.linkage_id) {
        const response = (await dispatch(
          putData(kpiLinkageDetailUrl(form.linkage_id), payload)
        )) as KPILinkage;
        if (response?.weight_warning) {
          handleShowToast(response.weight_warning, "info");
        }
        handleShowToast("Linkage updated successfully.", "success");
      } else {
        const response = (await dispatch(postData(kpiLinkagesUrl, payload))) as KPILinkage;
        if (response?.weight_warning) {
          handleShowToast(response.weight_warning, "info");
        }
        handleShowToast("Linkage created successfully.", "success");
      }
      setFormOpen(false);
      setForm(emptyForm);
      await fetchAll();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: any }; message?: string };
      const data = axiosError.response?.data;
      const errorMessage =
        (data && (data.child_kpi?.[0] || data.parent_kpi?.[0] || data.weight?.[0] || data.error)) ||
        axiosError.message ||
        "Failed to save linkage.";
      handleShowToast(errorMessage, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteData(kpiLinkageDetailUrl(deleteTarget.linkage_id)));
      handleShowToast("Linkage deleted successfully.", "success");
      setDeleteTarget(null);
      await fetchAll();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      handleShowToast(
        axiosError.response?.data?.error || axiosError.message || "Failed to delete linkage.",
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, mb: 10, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      <PageTitle>KPI Linkage Manager</PageTitle>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_event, newValue: number) => setActiveTab(newValue)}
          textColor="inherit"
          indicatorColor="secondary"
          sx={{ "& .MuiTab-root": { color: "black" }, "& .Mui-selected": { color: "black" } }}
        >
          <Tab label="Manage Linkages" />
          <Tab label="Hierarchy Tree" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Link existing KPIs into a simple parent-child hierarchy (e.g. CEO KPI → Department/Role KPIs →
              Team KPIs → Individual KPIs). Each linkage carries a weight used in the contribution formula:{" "}
              <strong>%Derived = (Rating / Threshold) × Weight</strong>. This is informational only and never
              changes actual ratings or statuses.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">Linkages</Typography>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={openCreateForm}
              >
                New Linkage
              </Button>
            </Box>

            {linkages.length === 0 ? (
              <Alert severity="info">No linkages created yet.</Alert>
            ) : (
              <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Child KPI</TableCell>
                      <TableCell>Parent KPI</TableCell>
                      <TableCell align="right">Weight</TableCell>
                      <TableCell>Cycle</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedLinkages.map((linkage) => (
                      <TableRow key={linkage.linkage_id} hover>
                        <TableCell>
                          <Tooltip title={linkage.child_kpi_title}>
                            <span style={{ fontWeight: 700 }}>
                              {linkage.child_kpi_code || linkage.child_kpi.slice(0, 8)}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={linkage.parent_kpi_title}>
                            <span style={{ fontWeight: 700 }}>
                              {linkage.parent_kpi_code || linkage.parent_kpi.slice(0, 8)}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end" }}>
                            <Chip
                              size="small"
                              label={Number(linkage.weight).toFixed(3)}
                              sx={{
                                fontWeight: 600,
                                borderRadius: "16px",
                                bgcolor: "#f0f0f0",
                              }}
                            />
                            {linkage.weight_warning && (
                              <Tooltip title={linkage.weight_warning}>
                                <Chip
                                  size="small"
                                  color="warning"
                                  label="!"
                                  sx={{ ml: 1, height: 18, minWidth: 18 }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {linkage.cycle_start_date && linkage.cycle_end_date
                            ? `${linkage.cycle_start_date} → ${linkage.cycle_end_date}`
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditForm(linkage)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(linkage)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <StandardTablePagination
                component="div"
                count={linkages.length}
                page={currentPage - 1}
                onPageChange={(_: unknown, newPage: number) => setCurrentPage(newPage + 1)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                sx={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
              />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "primary.main", fontSize: "1.05rem" }}>
              Hierarchy — CEO KPI → Department/Role KPIs → Team KPIs → Individual KPIs
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: { xs: "normal", md: "nowrap" }, pt: 0.3 }}
            >
              Click a row to expand — structure is read-only here. Edit weights in Manage Linkages.
            </Typography>
          </Box>
          <KPITreeView
            nodes={tree}
            selectedKpiId={selectedKpiId}
            onSelectNode={(node) => setSelectedKpiId(node.kpi_id)}
          />
        </Paper>
      )}

      {/* Create/Edit Linkage Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.linkage_id ? "Edit Linkage" : "New Linkage"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Child KPI</InputLabel>
              <Select
                label="Child KPI"
                value={form.child_kpi}
                onChange={handleSelectChange("child_kpi")}
              >
                {kpis.map((kpi) => (
                  <MenuItem key={kpi.kpi_id} value={kpi.kpi_id}>
                    {kpiLabel(kpi)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Parent KPI</InputLabel>
              <Select
                label="Parent KPI"
                value={form.parent_kpi}
                onChange={handleSelectChange("parent_kpi")}
              >
                {kpis.map((kpi) => (
                  <MenuItem key={kpi.kpi_id} value={kpi.kpi_id}>
                    {kpiLabel(kpi)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Weight"
              type="number"
              size="small"
              placeholder="e.g. 0.33"
              inputProps={{ min: 0, step: 0.001 }}
              value={form.weight}
              onChange={(e) => setForm((prev) => ({ ...prev, weight: e.target.value }))}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Cycle Start Date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.cycle_start_date}
                onChange={(e) => setForm((prev) => ({ ...prev, cycle_start_date: e.target.value }))}
              />
              <TextField
                label="Cycle End Date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.cycle_end_date}
                onChange={(e) => setForm((prev) => ({ ...prev, cycle_end_date: e.target.value }))}
              />
            </Box>

            {form.parent_kpi && kpiById[form.parent_kpi] && (
              <Typography variant="caption" color="text.secondary">
                Parent threshold: {kpiById[form.parent_kpi].threshold ?? "not set"}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Linkage</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the linkage between{" "}
            <strong>{deleteTarget?.child_kpi_code || deleteTarget?.child_kpi}</strong> and{" "}
            <strong>{deleteTarget?.parent_kpi_code || deleteTarget?.parent_kpi}</strong>? This action is
            audited and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KPILinkageManager;
